/**
 * PayLink POS Terminal Service
 * Communicates with Checkbox Desktop PayLink REST API (localhost)
 * Docs: https://wiki.checkbox.ua/uk/app/pc/portal_acquiring
 */

const DEFAULT_HOST = 'localhost';
const DEFAULT_PORT = 9020;
const PURCHASE_TIMEOUT = 120000; // 2 min — customer may need time to tap/insert card

let config = { host: DEFAULT_HOST, port: DEFAULT_PORT, deviceId: null };

/** Configure PayLink connection */
export function configure({ host, port, deviceId } = {}) {
  if (host) config.host = host;
  if (port) config.port = port;
  if (deviceId !== undefined) config.deviceId = deviceId;
}

/** Get current config */
export function getConfig() {
  return { ...config };
}

/** Check if PayLink is configured with a selected terminal */
export function isConfigured() {
  return !!(config.host && config.port && config.deviceId);
}

/** Base URL for PayLink API */
function baseUrl() {
  return `http://${config.host}:${config.port}`;
}

/** Fetch wrapper with timeout */
async function plFetch(path, options = {}) {
  const timeout = options.timeout || 10000;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeout);

  try {
    const res = await fetch(`${baseUrl()}${path}`, {
      ...options,
      signal: controller.signal,
      headers: { 'Content-Type': 'application/json', ...options.headers },
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      // PayLink errors come back as `{success:false, code, error, description}`.
      // Surface the human-readable `error` field directly so the user sees
      // "Відсутній зв'язок з терміналом..." instead of "PayLink API 503: {...}".
      let userMsg;
      try {
        const j = JSON.parse(text);
        if (j && typeof j.error === 'string' && j.error) userMsg = j.error;
      } catch { /* not JSON — fall through */ }
      throw new Error(userMsg || `PayLink API ${res.status}: ${text}`);
    }
    // PayLink occasionally returns 204 No Content or an empty body on
    // success — guard so we don't blow up trying to parse "".
    const text = await res.text();
    if (!text) return null;
    try { return JSON.parse(text); } catch { return text; }
  } catch (err) {
    if (err.name === 'AbortError') throw new Error('PayLink: час очікування вичерпано');
    if (err.message?.includes('Failed to fetch') || err.message?.includes('NetworkError')) {
      throw new Error('PayLink не доступний. Перевірте, що програма Checkbox Desktop PayLink запущена.');
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

/** Get list of registered terminals */
export async function getDevices() {
  return plFetch('/api/devices/');
}

/** Get specific terminal info */
export async function getDevice(deviceId) {
  return plFetch(`/api/devices/${deviceId}`);
}

/** Ping terminal (test connection) */
export async function ping(deviceId) {
  return plFetch(`/api/pos/${deviceId}/ping`);
}

/**
 * Initiate card payment on terminal
 * @param {number} amount - Amount in kopecks (100 = 1 UAH)
 * @param {string} [deviceId] - Terminal ID (uses configured device if omitted)
 * @returns {Promise<Object>} - PayLink purchase result with card_mask, rrn, auth_code etc.
 */
export async function purchase(amount, deviceId) {
  const did = deviceId || config.deviceId;
  if (!did) throw new Error('PayLink: термінал не обрано');

  const result = await plFetch(`/api/pos/${did}/purchase`, {
    method: 'POST',
    body: JSON.stringify({ amount }),
    timeout: PURCHASE_TIMEOUT,
  });

  if (!result.success) {
    const errMsg = result.error || result.result?.error || 'Оплата відхилена терміналом';
    throw new Error(`PayLink: ${errMsg}`);
  }

  return result.result;
}

/**
 * Check if PayLink is reachable
 * @returns {Promise<boolean>}
 */
export async function checkConnection() {
  try {
    await plFetch('/api/devices/', { timeout: 3000 });
    return true;
  } catch {
    return false;
  }
}
